
import React from 'react';
import { useAuth } from '@/context/AuthContext';
import { ViewType, CompanyUser, Lead } from '@/types/lead';
import { SortBy, LocationFilter, AREA_PRESETS, ContactStatusFilter } from '@/types/filters';
import { Button } from '@/components/ui/button';
import { Grid, Rows, Table2, Star, Trash2, ArrowUpDown, Sparkles, Loader2, X, MapPin, Building2, Mail, UserCheck, Copy } from 'lucide-react';
import { Input } from '@/components/ui/input';
import CategoryFilter from '@/components/CategoryFilter';
import CSVExportButton from '@/components/leads/csv/CSVExportButton';
import CSVImportButton from '@/components/leads/csv/CSVImportButton';
import MailMergeButton from '@/components/leads/mailmerge/MailMergeButton';
import ShareLeadsDialog from '@/components/leads/ShareLeadsDialog';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import UserLeadFilter from '@/components/UserLeadFilter';
import SectorFilter from '@/components/SectorFilter';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';

interface LeadsHeaderProps {
  currentView: ViewType;
  totalLeads: number;
  showFavoritesOnly: boolean;
  toggleFavoritesFilter: () => void;
  categories: string[];
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
  isLoading: boolean;
  sectors: string[];
  selectedSector: string | null;
  setSelectedSector: (sector: string | null) => void;
  companyUsers: CompanyUser[];
  selectedUserId: string | null;
  onUserSelect: (userId: string | null) => void;
  isLoadingUsers: boolean;
  filteredLeads: any[];
  onImportComplete?: () => void;
  selectedLeadIds?: Set<string>;
  onDeleteSelected?: () => void;
  sortBy: SortBy;
  onSortChange: (sort: SortBy) => void;
  hasEmail?: boolean;
  onHasEmailChange?: (value: boolean) => void;
  locationFilter: LocationFilter | null;
  onLocationFilterChange: (filter: LocationFilter | null) => void;
  onSmartMatch?: () => void;
  isAIFiltering?: boolean;
  isAIActive?: boolean;
  aiMode?: 'smart_match' | 'free_search' | null;
  onClearAI?: () => void;
  totalFilteredCount?: number;
  onSelectAllFiltered?: () => void;
  isSelectAllFiltered?: boolean;
  onFetchAllFilteredLeads?: () => Promise<Lead[]>;
  contactStatus?: ContactStatusFilter;
  onContactStatusChange?: (status: ContactStatusFilter) => void;
  showDuplicatesOnly?: boolean;
  onShowDuplicatesChange?: (value: boolean) => void;
}

const LeadsHeader: React.FC<LeadsHeaderProps> = ({
  currentView,
  totalLeads,
  showFavoritesOnly,
  toggleFavoritesFilter,
  categories,
  selectedCategories,
  setSelectedCategories,
  isLoading,
  companyUsers,
  selectedUserId,
  onUserSelect,
  isLoadingUsers,
  filteredLeads,
  onImportComplete,
  selectedLeadIds = new Set(),
  sectors,
  selectedSector,
  setSelectedSector,
  onDeleteSelected,
  sortBy,
  onSortChange,
  hasEmail,
  onHasEmailChange,
  locationFilter,
  onLocationFilterChange,
  onSmartMatch,
  isAIFiltering,
  isAIActive,
  aiMode,
  onClearAI,
  totalFilteredCount,
  onSelectAllFiltered,
  isSelectAllFiltered,
  onFetchAllFilteredLeads,
  contactStatus = 'all',
  onContactStatusChange,
  showDuplicatesOnly,
  onShowDuplicatesChange,
}) => {
  const { profile } = useAuth();
  const locationDisplayValue = locationFilter
    ? locationFilter.type === 'preset' && locationFilter.preset
      ? locationFilter.preset.label
      : locationFilter.type === 'custom' && locationFilter.customText
        ? `Custom: ${locationFilter.customText}`
        : ''
    : '';

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-5 mb-6 animate-fade-in">
        {/* Header with modern styling */}
        <div className="p-3 sm:p-4 md:p-5 rounded-xl glass-card flex items-center justify-between">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
            Leads
          </h2>
          <div className="flex items-center gap-1 sm:gap-2">
            {profile?.role === 'admin' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div><CSVImportButton onImportComplete={onImportComplete} /></div>
                </TooltipTrigger>
                <TooltipContent><p>Import leads from CSV file</p></TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <CSVExportButton 
                    leads={
                      currentView === 'table' && selectedLeadIds.size > 0 && !isSelectAllFiltered
                        ? filteredLeads.filter((l: any) => selectedLeadIds.has(l.id))
                        : filteredLeads
                    }
                    totalFilteredCount={
                      isSelectAllFiltered ? totalFilteredCount :
                      (currentView === 'table' && selectedLeadIds.size > 0 ? selectedLeadIds.size : totalLeads)
                    }
                    onFetchAllLeads={
                      isSelectAllFiltered
                        ? onFetchAllFilteredLeads
                        : (currentView === 'table' && selectedLeadIds.size > 0
                            ? undefined
                            : onFetchAllFilteredLeads)
                    }
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent><p>Export leads to CSV file</p></TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Action buttons row - only shown when leads are selected in table view */}
        {currentView === 'table' && selectedLeadIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-1 sm:gap-2 p-3 sm:p-4 rounded-xl glass-card animate-in fade-in slide-in-from-top-2">
            <Badge variant="secondary" className="text-xs whitespace-nowrap">
              {isSelectAllFiltered && totalFilteredCount ? totalFilteredCount : selectedLeadIds.size} selected
            </Badge>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <DeleteConfirmationDialog
                    entityName={`${selectedLeadIds.size} selected lead${selectedLeadIds.size > 1 ? 's' : ''}`}
                    onConfirm={() => onDeleteSelected?.()}
                    trigger={
                      <Button variant="destructive" size="sm" className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm gap-1">
                        <Trash2 className="h-4 w-4" />
                        <span className="hidden sm:inline">Delete</span>
                      </Button>
                    }
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent><p>Delete selected leads</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <MailMergeButton 
                    leads={filteredLeads.filter((l: any) => selectedLeadIds.has(l.id))}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent><p>Send mail merge to selected leads</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div><ShareLeadsDialog selectedLeadIds={selectedLeadIds} /></div>
              </TooltipTrigger>
              <TooltipContent><p>Share selected leads</p></TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Filters Section — Chip-style inline bar */}
        <div id="mobile-filters-section" className="flex flex-wrap items-start gap-2 scroll-mt-24">
          {/* Toggle chips */}
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <button
                onClick={toggleFavoritesFilter}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs border transition-all",
                  showFavoritesOnly
                    ? "bg-primary/20 border-primary/40 text-foreground hover:bg-primary/30"
                    : "bg-white/5 border-white/10 hover:bg-white/10 text-muted-foreground"
                )}
              >
                <Star className={cn("h-3 w-3", showFavoritesOnly && "fill-current")} />
                Favorites
                {showFavoritesOnly && (
                  <X className="h-3 w-3 ml-0.5 hover:text-foreground" onClick={(e) => { e.stopPropagation(); toggleFavoritesFilter(); }} />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>{showFavoritesOnly ? "Showing favorited leads" : "Show only favorited leads"}</TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <button
                onClick={() => onHasEmailChange?.(!hasEmail)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs border transition-all",
                  hasEmail
                    ? "bg-primary/20 border-primary/40 text-foreground hover:bg-primary/30"
                    : "bg-white/5 border-white/10 hover:bg-white/10 text-muted-foreground"
                )}
              >
                <Mail className={cn("h-3 w-3", hasEmail && "fill-current")} />
                Has Email
                {hasEmail && (
                  <X className="h-3 w-3 ml-0.5 hover:text-foreground" onClick={(e) => { e.stopPropagation(); onHasEmailChange?.(false); }} />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>{hasEmail ? "Showing leads with emails" : "Show only leads with email addresses"}</TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <button
                onClick={() => onShowDuplicatesChange?.(!showDuplicatesOnly)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs border transition-all",
                  showDuplicatesOnly
                    ? "bg-primary/20 border-primary/40 text-foreground hover:bg-primary/30"
                    : "bg-white/5 border-white/10 hover:bg-white/10 text-muted-foreground"
                )}
              >
                <Copy className={cn("h-3 w-3", showDuplicatesOnly && "fill-current")} />
                Duplicates
                {showDuplicatesOnly && (
                  <X className="h-3 w-3 ml-0.5 hover:text-foreground" onClick={(e) => { e.stopPropagation(); onShowDuplicatesChange?.(false); }} />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>{showDuplicatesOnly ? "Showing duplicate leads" : "Show potential duplicate leads"}</TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <button
                onClick={isAIActive ? onClearAI : onSmartMatch}
                disabled={isAIFiltering}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs border transition-all",
                  isAIActive && aiMode === 'smart_match'
                    ? "bg-primary/20 border-primary/40 text-foreground hover:bg-primary/30"
                    : "bg-white/5 border-white/10 hover:bg-white/10 text-muted-foreground",
                  isAIFiltering && "opacity-50 pointer-events-none"
                )}
              >
                {isAIFiltering && aiMode === 'smart_match' ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className={cn("h-3 w-3", isAIActive && aiMode === 'smart_match' && "fill-current")} />
                )}
                Smart Match
                {isAIActive && aiMode === 'smart_match' && (
                  <X className="h-3 w-3 ml-0.5 hover:text-foreground" onClick={(e) => { e.stopPropagation(); onClearAI?.(); }} />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>{isAIActive && aiMode === 'smart_match' ? "Showing AI-matched leads" : "Find leads that best match your company profile using AI"}</TooltipContent>
          </Tooltip>

          {/* Divider */}
          <div className="w-px h-6 bg-white/10 self-center mx-0.5" />

          {/* Dropdown filters */}
          <CategoryFilter
            categories={categories}
            selectedCategories={selectedCategories}
            onSelectCategories={setSelectedCategories}
            isLoading={isLoading}
          />

          <SectorFilter
            sectors={sectors}
            selectedSector={selectedSector}
            onSelectSector={setSelectedSector}
            isLoading={isLoading}
          />

          <UserLeadFilter
            companyUsers={companyUsers}
            selectedUserId={selectedUserId}
            onUserSelect={onUserSelect}
            isLoading={isLoadingUsers}
          />

          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <div className="flex flex-col gap-1.5">
                <Select
                  value={locationFilter?.type === 'preset' && locationFilter.preset ? locationFilter.preset.label : '__none__'}
                  onValueChange={(val) => {
                    if (val === '__none__') {
                      onLocationFilterChange(null);
                    } else if (val === '__custom__') {
                      onLocationFilterChange({ type: 'custom', customText: '' });
                    } else {
                      const preset = AREA_PRESETS.find(p => p.label === val);
                      if (preset) {
                        onLocationFilterChange({ type: 'preset', preset });
                      }
                    }
                  }}
                >
                  <SelectTrigger className={cn(
                    "rounded-full h-8 px-3 text-xs border transition-all w-auto min-w-[100px]",
                    locationFilter
                      ? "bg-primary/20 border-primary/40 text-foreground hover:bg-primary/30"
                      : "bg-white/5 border-white/10 hover:bg-white/10 text-muted-foreground"
                  )}>
                    <MapPin className="h-3 w-3 mr-1.5" />
                    <SelectValue placeholder="Area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">All Areas</SelectItem>
                    {AREA_PRESETS.map(preset => (
                      <SelectItem key={preset.label} value={preset.label}>
                        {preset.label}
                        <span className="text-muted-foreground ml-1">({preset.postcodes.length})</span>
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom__">Custom...</SelectItem>
                  </SelectContent>
                </Select>
                {locationFilter?.type === 'custom' && (
                  <div className="relative">
                    <Input
                      value={locationFilter.customText || ''}
                      onChange={(e) => onLocationFilterChange({ type: 'custom', customText: e.target.value })}
                      placeholder="e.g. D22, Tallaght..."
                      className="w-[160px] h-7 text-xs rounded-full px-3 pr-7"
                      autoFocus
                    />
                    {locationFilter.customText && (
                      <button
                        onClick={() => onLocationFilterChange(null)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
                {locationFilter?.type === 'preset' && locationFilter.preset && (
                  <HoverCard openDelay={200}>
                    <HoverCardTrigger asChild>
                      <div className="flex items-center gap-1 max-w-[180px] cursor-default">
                        {locationFilter.preset.postcodes.slice(0, 4).map(pc => (
                          <Badge key={pc} variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                            {pc}
                          </Badge>
                        ))}
                        {locationFilter.preset.postcodes.length > 4 && (
                          <span className="text-[10px] text-muted-foreground shrink-0">
                            +{locationFilter.preset.postcodes.length - 4}
                          </span>
                        )}
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-auto max-w-[240px] p-3" side="bottom" align="start">
                      <div className="flex flex-wrap gap-1">
                        {locationFilter.preset.postcodes.map(pc => (
                          <Badge key={pc} variant="secondary" className="text-[10px] px-1.5 py-0">
                            {pc}
                          </Badge>
                        ))}
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>Filter leads by geographic area</TooltipContent>
          </Tooltip>

          <Select value={contactStatus} onValueChange={(v) => onContactStatusChange?.(v as ContactStatusFilter)}>
            <SelectTrigger className={cn(
              "rounded-full h-8 px-3 text-xs border transition-all w-auto min-w-[100px]",
              contactStatus && contactStatus !== 'all'
                ? "bg-primary/20 border-primary/40 text-foreground hover:bg-primary/30"
                : "bg-white/5 border-white/10 hover:bg-white/10 text-muted-foreground"
            )}>
              <UserCheck className="h-3 w-3 mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="not_contacted">Not Contacted</SelectItem>
              <SelectItem value="not_contacted_30">Not contacted (30d)</SelectItem>
              <SelectItem value="not_contacted_60">Not contacted (60d)</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={(v) => onSortChange(v as SortBy)}>
            <SelectTrigger className="rounded-full h-8 px-3 text-xs border bg-white/5 border-white/10 hover:bg-white/10 text-muted-foreground transition-all w-auto min-w-[100px]">
              <ArrowUpDown className="h-3 w-3 mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
              <SelectItem value="name_asc">Name (A→Z)</SelectItem>
              <SelectItem value="name_desc">Name (Z→A)</SelectItem>
              <SelectItem value="quality_high">Quality (High→Low)</SelectItem>
              <SelectItem value="quality_low">Quality (Low→High)</SelectItem>
              <SelectItem value="review_score">Review Score</SelectItem>
              <SelectItem value="last_contacted">Last Contacted</SelectItem>
              <SelectItem value="last_contacted_asc">Contacted (Oldest)</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear All — only when filters are active */}
          {(showFavoritesOnly || hasEmail || showDuplicatesOnly || (isAIActive && aiMode === 'smart_match') || selectedCategories.length > 0 || selectedSector || selectedUserId || locationFilter || (contactStatus && contactStatus !== 'all')) && (
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full h-8 px-3 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    if (showFavoritesOnly) toggleFavoritesFilter();
                    onHasEmailChange?.(false);
                    onShowDuplicatesChange?.(false);
                    if (isAIActive) onClearAI?.();
                    setSelectedCategories([]);
                    setSelectedSector(null);
                    onUserSelect(null);
                    onLocationFilterChange(null);
                    onContactStatusChange?.('all');
                  }}
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear all
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset all filters</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Cross-page select all banner */}
        {currentView === 'table' && selectedLeadIds.size > 0 && totalFilteredCount && totalFilteredCount > selectedLeadIds.size && (
          <div className="flex items-center justify-center gap-2 p-2 rounded-lg bg-primary/5 border border-primary/20 text-sm">
            <span className="text-muted-foreground">
              {selectedLeadIds.size} leads selected on this page.
            </span>
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-primary font-semibold"
              onClick={onSelectAllFiltered}
            >
              Select all {totalFilteredCount} matching leads
            </Button>
          </div>
        )}
        {isSelectAllFiltered && totalFilteredCount && (
          <div className="flex items-center justify-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20 text-sm">
            <span className="font-medium text-foreground">
              All {totalFilteredCount} matching leads are selected.
            </span>
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-destructive font-semibold"
              onClick={() => onSelectAllFiltered?.()}
            >
              Clear selection
            </Button>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default LeadsHeader;
